import { Badge, Button, Input, Label, cn } from '@lawbee/ui';
import { Check, Copy, Lock, Plus, Trash2 } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { minutesToTime, timeToMinutes, useCopy, WEEKDAYS } from '../../../lib/hooks';
import type { AvailabilityRule, Destination } from '../../../lib/types';

// ---------------------------------------------------------------------------
// Switch (accessible toggle; the design system has Checkbox but no switch)
// ---------------------------------------------------------------------------

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  id,
  className,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}) {
  const autoId = useId();
  const sid = id ?? autoId;
  const button = (
    <button
      type="button"
      role="switch"
      id={sid}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-foreground/15',
      )}
    >
      <span className={cn('inline-block size-5 rounded-full bg-card shadow transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-0.5')} />
    </button>
  );
  if (!label) return <span className={className}>{button}</span>;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={sid} className="text-sm">
          {label}
        </Label>
        {description ? <p className="text-xs leading-relaxed text-muted-foreground">{description}</p> : null}
      </div>
      {button}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy helpers
// ---------------------------------------------------------------------------

export function CopyButton({ value, label = 'Copy', copiedLabel = 'Link copied', size = 'sm', variant = 'outline' }: { value: string; label?: string; copiedLabel?: string; size?: 'sm' | 'md'; variant?: 'outline' | 'ghost' | 'secondary' }) {
  const { copy, copied } = useCopy();
  const done = copied === value;
  return (
    <Button type="button" variant={variant} size={size} onClick={() => void copy(value, copiedLabel)} aria-label={`${label}: ${value}`}>
      {done ? <Check /> : <Copy />}
      {done ? 'Copied' : label}
    </Button>
  );
}

/** Read-only URL with a copy button. */
export function CopyField({ label, value, description }: { label: string; value: string; description?: ReactNode }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input id={id} readOnly value={value} className="figures min-w-0 text-[13px]" onFocus={(e) => e.currentTarget.select()} />
        <CopyButton value={value} size="md" />
      </div>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

/** Shown on settings sections a non-owner can see but not change. */
export function ReadOnlyNote({ children = 'Only an owner can change these settings.' }: { children?: ReactNode }) {
  return (
    <p className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-[13px] text-muted-foreground">
      <Lock className="size-3.5 shrink-0" aria-hidden />
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export function DestinationBadge({ destination }: { destination: Destination }) {
  return destination === 'trust' ? <Badge variant="info">Trust</Badge> : <Badge variant="outline">Operating</Badge>;
}

const INVOICE_VARIANT: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info' | 'outline'> = {
  draft: 'default',
  open: 'primary',
  paid: 'success',
  void: 'outline',
  partially_paid: 'warning',
};

export function InvoiceStatusBadge({ status }: { status: string }) {
  return <Badge variant={INVOICE_VARIANT[status] ?? 'default'}>{humanizeStatus(status)}</Badge>;
}

const BOOKING_VARIANT: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info' | 'outline'> = {
  confirmed: 'primary',
  pending_payment: 'warning',
  completed: 'success',
  cancelled: 'outline',
  no_show: 'destructive',
};

export function BookingStatusBadge({ status }: { status: string }) {
  return <Badge variant={BOOKING_VARIANT[status] ?? 'default'}>{status === 'no_show' ? 'No-show' : humanizeStatus(status)}</Badge>;
}

function humanizeStatus(s: string): string {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// ---------------------------------------------------------------------------
// Weekly availability editor (onboarding and booking settings)
// ---------------------------------------------------------------------------

/** Monday first, Sunday last (weekday numbers: 0 = Sunday). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export const DEFAULT_RULES: AvailabilityRule[] = [1, 2, 3, 4, 5].map((weekday) => ({ weekday, startMin: 9 * 60, endMin: 17 * 60 }));

export function sortRules(rules: AvailabilityRule[]): AvailabilityRule[] {
  const order = (d: number) => (d === 0 ? 7 : d);
  return [...rules].sort((a, b) => order(a.weekday) - order(b.weekday) || a.startMin - b.startMin);
}

/**
 * Weekday rows with an enabled toggle and one or more start/end ranges. `multiple` allows extra
 * ranges per day (e.g. a lunch break).
 */
export function AvailabilityEditor({
  rules,
  onChange,
  disabled,
  multiple = true,
}: {
  rules: AvailabilityRule[];
  onChange: (rules: AvailabilityRule[]) => void;
  disabled?: boolean;
  multiple?: boolean;
}) {
  const byDay = (d: number) => rules.filter((r) => r.weekday === d).sort((a, b) => a.startMin - b.startMin);
  const setDay = (d: number, ranges: AvailabilityRule[]) => onChange(sortRules([...rules.filter((r) => r.weekday !== d), ...ranges]));

  return (
    <div className="divide-y divide-border/70 rounded-xl border border-border/70">
      {WEEK_ORDER.map((d) => {
        const ranges = byDay(d);
        const enabled = ranges.length > 0;
        const dayName = WEEKDAYS[d] ?? '';
        return (
          <div key={d} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start">
            <div className="flex w-full items-center justify-between gap-3 sm:w-44 sm:shrink-0 sm:justify-start sm:pt-2">
              <Switch
                checked={enabled}
                disabled={disabled}
                onCheckedChange={(on) => setDay(d, on ? [{ weekday: d, startMin: 9 * 60, endMin: 17 * 60 }] : [])}
                id={`avail-${d}`}
              />
              <label htmlFor={`avail-${d}`} className="flex-1 text-sm font-medium sm:flex-none">
                {dayName}
              </label>
              {!enabled ? <span className="text-[13px] text-muted-foreground sm:hidden">Closed</span> : null}
            </div>
            <div className="flex flex-1 flex-col gap-2">
              {!enabled ? <span className="hidden pt-2 text-[13px] text-muted-foreground sm:block">Closed</span> : null}
              {ranges.map((r, i) => {
                const invalid = r.startMin >= r.endMin;
                return (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <Input
                      type="time"
                      step={900}
                      aria-label={`${dayName} start time${ranges.length > 1 ? ` ${i + 1}` : ''}`}
                      className="figures w-32"
                      value={minutesToTime(r.startMin)}
                      disabled={disabled}
                      aria-invalid={invalid || undefined}
                      onChange={(e) => e.target.value && setDay(d, ranges.map((x, j) => (j === i ? { ...x, startMin: timeToMinutes(e.target.value) } : x)))}
                    />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input
                      type="time"
                      step={900}
                      aria-label={`${dayName} end time${ranges.length > 1 ? ` ${i + 1}` : ''}`}
                      className="figures w-32"
                      value={minutesToTime(r.endMin)}
                      disabled={disabled}
                      aria-invalid={invalid || undefined}
                      onChange={(e) => e.target.value && setDay(d, ranges.map((x, j) => (j === i ? { ...x, endMin: timeToMinutes(e.target.value) } : x)))}
                    />
                    {multiple && ranges.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-9"
                        disabled={disabled}
                        aria-label={`Remove ${dayName} range ${i + 1}`}
                        onClick={() => setDay(d, ranges.filter((_, j) => j !== i))}
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                    {invalid ? <span className="w-full text-xs text-destructive">Start must be before end.</span> : null}
                  </div>
                );
              })}
              {multiple && enabled ? (
                <div>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    disabled={disabled}
                    onClick={() => {
                      const last = ranges[ranges.length - 1];
                      const start = Math.min(last ? last.endMin + 60 : 13 * 60, 23 * 60);
                      setDay(d, [...ranges, { weekday: d, startMin: start, endMin: Math.min(start + 60, 24 * 60 - 1) }]);
                    }}
                  >
                    <Plus /> Add hours
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Lowercase snake key from a label (stage keys must match ^[a-z][a-z0-9_]{0,39}$). */
export function slugKey(label: string): string {
  const s = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!s) return 'stage';
  return /^[a-z]/.test(s) ? s : `s_${s}`.slice(0, 40);
}
